import { useTranslation } from "react-i18next";

export const getErrorMessage = (error, t) => {

    const technicalMessages = [
        "Internal Server Error",
        "Not Found",
        "Bad Request",
        "Unauthorized",
        "Forbidden",
        "Method Not Allowed",
        "Request Timeout",
        "Conflict",
        "Validation Error",
        "Too Many Requests",
        "Bad Gateway",
        "Service Unavailable",
        "Gateway Timeout",
        "Unexpected token",
        "Unexpected end of JSON input"
    ];

    // Extraire le code d'erreur si disponible
    const errorCode = error.status || error.code || null;
    const isApiError = errorCode && errorCode >= 400 && errorCode < 600;

    // Si l'erreur a un message spécifique
    if (error.message) {
        const msg = error.message.toLowerCase();

        // Mapping des messages d'erreur API vers les clés de traduction
        const errorMapping = {
            "stripe customer not found and could not be created": "errors.stripeCustomerNotFound",
            "country not found": "errors.countryNotFound",
            "postal code not found": "errors.postalCodeNotFound",
            "no ongoing transaction found for this user": "errors.noOngoingTransaction",
            "transaction has already been closed": "errors.transactionClosed",
            "no credit card authorization found for this transaction": "errors.noAuthorizationFound",
            "authorisation not found": "errors.authorizationNotFound",
            "authorisation not found in db": "errors.authorizationNotFoundInDB",
            "error updating authorisation": "errors.updatingAuthorizationError",
            "user has no default credit card": "errors.noDefaultCreditCard",
            "no ongoing transaction for this user": "errors.noOngoingTransaction",
            "evcp with reference not found": "errors.evcpNotFound",
            "borne has no connector": "errors.connectorNotFound",
            "useridtag not found": "errors.useridTagNotFound",
            "failed to associate user": "errors.failedAssociateUser",
            "internal error. please try again": "errors.internalError",
            "no ongoing cp session for this user": "errors.noOngoingCpSession",
            "remote start denied": "errors.remoteStartDenied",
            "unexpected error sending to kafka": "errors.kafkaError",
            "transaction is not associated to evcp": "errors.transactionNotAssociated",
            "cp of transaction is disconnected": "errors.cpDisconnected"
        };

        // Chercher une correspondance exacte ou partielle
        for (const [apiMsg, translationKey] of Object.entries(errorMapping)) {
            if (msg.includes(apiMsg)) {
                const translatedMessage = t(translationKey);
                // Ajouter le code d'erreur entre parenthèses si c'est une erreur API
                return isApiError ? `${translatedMessage} (code de l'erreur: ${errorCode})` : translatedMessage;
            }
        }

        // Si le message n'est pas dans la liste technique, on peut l'afficher directement
        const isTechnical = technicalMessages.some(techMsg =>
            msg.includes(techMsg.toLowerCase())
        );

        if (!isTechnical) {
            // Ajouter le code d'erreur entre parenthèses si c'est une erreur API
            return isApiError ? `${error.message} (code de l'erreur: ${errorCode})` : error.message;
        }

        // Si c'est technique et non mappé, on continue pour essayer de trouver un message par code de statut
    }

    // Si l'erreur est une réponse HTTP
    if (error.status) {
        const status = error.status;
        let translatedMessage;
        
        switch (status) {
            case 400:
                translatedMessage = t("errors.badRequest");
                break;
            case 401:
                translatedMessage = t("errors.unauthorized");
                break;
            case 403:
                translatedMessage = t("errors.forbidden");
                break;
            case 404:
                translatedMessage = t("errors.notFound");
                break;
            case 405:
                translatedMessage = t("errors.methodNotAllowed");
                break;
            case 408:
                translatedMessage = t("errors.requestTimeout");
                break;
            case 409:
                translatedMessage = t("errors.conflict");
                break;
            case 422:
                translatedMessage = t("errors.validationError");
                break;
            case 429:
                translatedMessage = t("errors.tooManyRequests");
                break;
            case 500:
                translatedMessage = t("errors.serverError");
                break;
            case 502:
                translatedMessage = t("errors.badGateway");
                break;
            case 503:
                translatedMessage = t("errors.serviceUnavailable");
                break;
            case 504:
                translatedMessage = t("errors.gatewayTimeout");
                break;
            default:
                translatedMessage = t("errors.unknownError");
        }
        
        // Ajouter le code d'erreur entre parenthèses
        return `${translatedMessage} (code de l'erreur: ${status})`;
    }

    // Si l'erreur est un probleme reseau
    if (error.message && (error.message.includes("Network Error") || error.message.includes("failed to fetch"))) {
        return t("errors.networkError");
    }

    // par défaut
    return t("errors.unknownError");
};

export const createUserError = (technicalError, userMessage) => {
    const error = new Error(technicalError);
    error.userMessage = userMessage;
    return error;
};

// Hook pour gérer les erreurs dans les composants
export const useErrorHandler = () => {
    const { t } = useTranslation();

    const handleError = (error) => {
        const message = getErrorMessage(error, t);
        console.error("Erreur technique:", error);
        return message;
    };

    return { handleError };
};